import {
  BadRequestException,
  Body,
  ConflictException,
  Controller,
  Delete,
  Get,
  Injectable,
  Module,
  Param,
  Patch,
  Post,
} from '@nestjs/common';
import { ApiBearerAuth, ApiProperty, ApiTags } from '@nestjs/swagger';
import { Type } from 'class-transformer';
import {
  ArrayMinSize,
  IsArray,
  IsDateString,
  IsEnum,
  IsInt,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Min,
  ValidateNested,
} from 'class-validator';
import {
  MovementType,
  PaymentMethod,
  Role,
  TransactionStatus,
} from '@sakura/prisma/enums';
import { Prisma } from '@sakura/prisma';
import {
  CurrentUser,
  CurrentUserData,
  Permissions,
  Roles,
} from '../common/security';
import { PrismaService } from '../prisma/prisma.module';

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function calculateLineTotal(
  quantity: number,
  unitPrice: number,
  discount: number,
  vat: number,
): number {
  const gross = roundMoney(quantity * unitPrice);
  if (discount > gross + vat) {
    throw new BadRequestException(
      'Line discount cannot exceed gross amount plus VAT',
    );
  }
  return roundMoney(gross - discount + vat);
}

class PurchaseItemDto {
  @IsUUID()
  productId!: string;
  @IsString()
  batchNumber!: string;
  @IsDateString()
  expiryDate!: string;
  @IsInt()
  @Min(1)
  quantity!: number;
  @IsOptional()
  @IsInt()
  @Min(0)
  bonusQuantity = 0;
  @IsNumber()
  @Min(0)
  unitPrice!: number;
  @IsOptional()
  @IsNumber()
  @Min(0)
  discount = 0;
  @IsOptional()
  @IsNumber()
  @Min(0)
  vat = 0;
}

class CreatePurchaseDto {
  @ApiProperty()
  @IsString()
  invoiceNumber!: string;
  @IsUUID()
  companyId!: string;
  @IsUUID()
  branchId!: string;
  @IsDateString()
  invoiceDate!: string;
  @IsOptional()
  @IsString()
  supplierRef?: string;
  @IsOptional()
  @IsDateString()
  slipDate?: string;
  @IsOptional()
  @IsString()
  purchaseType?: string;
  @IsOptional()
  @IsString()
  remarks?: string;
  @IsOptional()
  @IsDateString()
  dueDate?: string;
  @IsArray()
  @ArrayMinSize(1)
  @ValidateNested({ each: true })
  @Type(() => PurchaseItemDto)
  items!: PurchaseItemDto[];
}

class UpdatePurchaseDto extends CreatePurchaseDto {
  @IsNumber()
  @Min(0)
  paidAmount!: number;
}

class PurchasePaymentDto {
  @IsEnum(PaymentMethod)
  method!: PaymentMethod;
  @IsNumber()
  @Min(0.01)
  amount!: number;
  @IsOptional()
  @IsString()
  reference?: string;
  @IsOptional()
  @IsString()
  notes?: string;
  @IsOptional()
  @IsDateString()
  paidAt?: string;
}

@Injectable()
export class PurchasesService {
  constructor(private readonly prisma: PrismaService) {}

  list() {
    return this.prisma.purchase.findMany({
      include: { items: true, company: true },
      orderBy: { createdAt: 'desc' },
    });
  }

  create(dto: CreatePurchaseDto, userId: string) {
    const { items, subtotal, discount, vat, total } =
      this.preparePurchase(dto);
    return this.prisma.purchase.create({
      data: {
        invoiceNumber: dto.invoiceNumber,
        companyId: dto.companyId,
        branchId: dto.branchId,
        invoiceDate: new Date(dto.invoiceDate),
        supplierRef: dto.supplierRef,
        slipDate: dto.slipDate ? new Date(dto.slipDate) : undefined,
        purchaseType: dto.purchaseType,
        remarks: dto.remarks,
        dueDate: dto.dueDate ? new Date(dto.dueDate) : undefined,
        subtotal,
        discount,
        vat,
        total,
        createdById: userId,
        items: { create: items },
      },
      include: { items: true },
    });
  }

  confirm(id: string) {
    return this.prisma.$transaction(
      async (tx) => {
        const purchase = await tx.purchase.findUniqueOrThrow({
          where: { id },
          include: { items: true },
        });
        if (purchase.status !== TransactionStatus.DRAFT) {
          throw new ConflictException('Only draft purchases can be confirmed');
        }
        for (const item of purchase.items) {
          const quantity = item.quantity + item.bonusQuantity;
          const effectiveUnitCost = roundMoney(Number(item.total) / quantity);
          const batchKey = {
            productId: item.productId,
            branchId: purchase.branchId,
            batchNumber: item.batchNumber,
          };
          const existingBatch = await tx.productBatch.findUnique({
            where: { productId_branchId_batchNumber: batchKey },
          });
          const product = existingBatch
            ? null
            : await tx.product.findUniqueOrThrow({
              where: { id: item.productId },
              select: { salePrice: true },
            });
          const balance = (existingBatch?.quantity ?? 0) + quantity;
          const weightedCost = existingBatch
            ? roundMoney(
                (existingBatch.quantity *
                  Number(existingBatch.purchasePrice) +
                  quantity * effectiveUnitCost) /
                  balance,
              )
            : effectiveUnitCost;
          const batch = existingBatch
            ? await tx.productBatch.update({
                where: { id: existingBatch.id },
                data: {
                  quantity: balance,
                  purchasePrice: weightedCost,
                  expiryDate: item.expiryDate,
                },
              })
            : await tx.productBatch.create({
                data: {
                  ...batchKey,
                  expiryDate: item.expiryDate,
                  quantity,
                  purchasePrice: effectiveUnitCost,
                  salePrice: product?.salePrice ?? effectiveUnitCost,
                },
              });
          await tx.stockMovement.create({
            data: {
              productId: item.productId,
              branchId: purchase.branchId,
              type: MovementType.PURCHASE,
              quantity,
              balanceAfter: batch.quantity,
              unitCost: effectiveUnitCost,
              referenceType: 'Purchase',
              referenceId: purchase.id,
            },
          });
        }
        return tx.purchase.update({
          where: { id },
          data: { status: TransactionStatus.CONFIRMED },
          include: { items: true },
        });
      },
      { isolationLevel: Prisma.TransactionIsolationLevel.Serializable },
    );
  }

  update(id: string, dto: UpdatePurchaseDto) {
    const prepared = this.preparePurchase(dto);
    if (dto.paidAmount > prepared.total) {
      throw new BadRequestException('Paid amount cannot exceed purchase total');
    }

    return this.prisma.$transaction(
      async (tx) => {
        const purchase = await tx.purchase.findUniqueOrThrow({
          where: { id },
          include: { items: true },
        });
        if (purchase.status !== TransactionStatus.CONFIRMED) {
          throw new ConflictException('Only confirmed purchases can be edited');
        }
        if (purchase.branchId !== dto.branchId) {
          throw new BadRequestException('Purchase branch cannot be changed');
        }

        type StockChange = {
          productId: string;
          batchNumber: string;
          quantity: number;
          newQuantity: number;
          addedCost: number;
          expiryDate?: Date;
        };
        const changes = new Map<string, StockChange>();
        const addChange = (
          productId: string,
          batchNumber: string,
          quantity: number,
          unitCost: number,
          expiryDate?: Date,
        ) => {
          const key = `${productId}:${batchNumber}`;
          const current = changes.get(key) ?? {
            productId,
            batchNumber,
            quantity: 0,
            newQuantity: 0,
            addedCost: 0,
          };
          current.quantity += quantity;
          if (quantity > 0) {
            current.newQuantity += quantity;
            current.addedCost += quantity * unitCost;
            current.expiryDate = expiryDate;
          }
          changes.set(key, current);
        };

        for (const item of purchase.items) {
          addChange(
            item.productId,
            item.batchNumber,
            -(item.quantity + item.bonusQuantity),
            0,
          );
        }
        for (const item of prepared.items) {
          const quantity = item.quantity + item.bonusQuantity;
          addChange(
            item.productId,
            item.batchNumber,
            quantity,
            roundMoney(Number(item.total) / quantity),
            item.expiryDate,
          );
        }

        for (const change of changes.values()) {
          if (change.quantity === 0) continue;
          const batchKey = {
            productId: change.productId,
            branchId: purchase.branchId,
            batchNumber: change.batchNumber,
          };
          const batch = await tx.productBatch.findUnique({
            where: { productId_branchId_batchNumber: batchKey },
          });
          if (change.quantity < 0) {
            const deduction = Math.abs(change.quantity);
            if (!batch || batch.quantity < deduction) {
              throw new ConflictException(
                `Cannot reduce ${change.batchNumber}; some stock has already been used`,
              );
            }
            const updated = await tx.productBatch.update({
              where: { id: batch.id },
              data: { quantity: { decrement: deduction } },
            });
            await tx.stockMovement.create({
              data: {
                productId: change.productId,
                branchId: purchase.branchId,
                type: MovementType.PURCHASE,
                quantity: -deduction,
                balanceAfter: updated.quantity,
                referenceType: 'PurchaseEdit',
                referenceId: purchase.id,
                notes: 'Purchase quantity reduced by edit',
              },
            });
            continue;
          }

          const addedUnitCost = roundMoney(
            change.addedCost / change.newQuantity,
          );
          const product = batch
            ? null
            : await tx.product.findUniqueOrThrow({
                where: { id: change.productId },
                select: { salePrice: true },
              });
          const nextQuantity = (batch?.quantity ?? 0) + change.quantity;
          const purchasePrice = batch
            ? roundMoney(
                (batch.quantity * Number(batch.purchasePrice) +
                  change.quantity * addedUnitCost) /
                  nextQuantity,
              )
            : addedUnitCost;
          const updated = batch
            ? await tx.productBatch.update({
                where: { id: batch.id },
                data: {
                  quantity: nextQuantity,
                  purchasePrice,
                  ...(change.expiryDate
                    ? { expiryDate: change.expiryDate }
                    : {}),
                },
              })
            : await tx.productBatch.create({
                data: {
                  ...batchKey,
                  expiryDate: change.expiryDate ?? new Date('2099-12-31'),
                  quantity: change.quantity,
                  purchasePrice,
                  salePrice: product?.salePrice ?? purchasePrice,
                },
              });
          await tx.stockMovement.create({
            data: {
              productId: change.productId,
              branchId: purchase.branchId,
              type: MovementType.PURCHASE,
              quantity: change.quantity,
              balanceAfter: updated.quantity,
              unitCost: addedUnitCost,
              referenceType: 'PurchaseEdit',
              referenceId: purchase.id,
              notes: 'Purchase quantity increased by edit',
            },
          });
        }

        await tx.payment.deleteMany({ where: { purchaseId: id } });
        if (dto.paidAmount > 0) {
          await tx.payment.create({
            data: {
              purchaseId: id,
              method: PaymentMethod.CASH,
              amount: dto.paidAmount,
              reference: 'Purchase edit',
            },
          });
        }
        await tx.purchaseItem.deleteMany({ where: { purchaseId: id } });
        return tx.purchase.update({
          where: { id },
          data: {
            invoiceNumber: dto.invoiceNumber,
            companyId: dto.companyId,
            invoiceDate: new Date(dto.invoiceDate),
            supplierRef: dto.supplierRef,
            slipDate: dto.slipDate ? new Date(dto.slipDate) : null,
            purchaseType: dto.purchaseType,
            remarks: dto.remarks,
            dueDate: dto.dueDate ? new Date(dto.dueDate) : null,
            subtotal: prepared.subtotal,
            discount: prepared.discount,
            vat: prepared.vat,
            total: prepared.total,
            paidAmount: dto.paidAmount,
            items: { create: prepared.items },
          },
          include: { items: true, company: true },
        });
      },
      { isolationLevel: Prisma.TransactionIsolationLevel.Serializable },
    );
  }

  remove(id: string) {
    return this.prisma.$transaction(
      async (tx) => {
        const purchase = await tx.purchase.findUniqueOrThrow({
          where: { id },
          include: { items: true },
        });
        if (purchase.status !== TransactionStatus.CONFIRMED) {
          throw new ConflictException(
            'Only confirmed purchases can be deleted',
          );
        }
        for (const item of purchase.items) {
          const quantity = item.quantity + item.bonusQuantity;
          const batch = await tx.productBatch.findUnique({
            where: {
              productId_branchId_batchNumber: {
                productId: item.productId,
                branchId: purchase.branchId,
                batchNumber: item.batchNumber,
              },
            },
          });
          if (!batch || batch.quantity < quantity) {
            throw new ConflictException(
              `Cannot delete purchase; stock from batch ${item.batchNumber} has already been used`,
            );
          }
          await tx.productBatch.update({
            where: { id: batch.id },
            data: { quantity: { decrement: quantity } },
          });
        }
        await tx.payment.deleteMany({ where: { purchaseId: id } });
        await tx.stockMovement.deleteMany({
          where: { referenceId: id, referenceType: { startsWith: 'Purchase' } },
        });
        await tx.purchase.delete({ where: { id } });
        return { id };
      },
      { isolationLevel: Prisma.TransactionIsolationLevel.Serializable },
    );
  }

  listPayments(id: string) {
    return this.prisma.payment.findMany({
      where: { purchaseId: id },
      include: {
        paidBy: {
          select: { id: true, firstName: true, lastName: true, email: true },
        },
      },
      orderBy: { paidAt: 'desc' },
    });
  }

  pay(id: string, dto: PurchasePaymentDto, userId: string) {
    return this.prisma.$transaction(async (tx) => {
      const purchase = await tx.purchase.findUniqueOrThrow({
        where: { id },
        include: { company: true },
      });
      if (purchase.status !== TransactionStatus.CONFIRMED) {
        throw new ConflictException(
          'Only confirmed purchases can receive payments',
        );
      }
      const due = roundMoney(
        Number(purchase.total) - Number(purchase.paidAmount),
      );
      if (dto.amount > due) {
        throw new BadRequestException(
          `Payment exceeds outstanding due of ${due}`,
        );
      }
      const payment = await tx.payment.create({
        data: {
          purchaseId: id,
          method: dto.method,
          amount: dto.amount,
          reference: dto.reference?.trim() || undefined,
          notes: dto.notes?.trim() || undefined,
          paidAt: dto.paidAt ? new Date(dto.paidAt) : undefined,
          paidById: userId,
        },
        include: {
          paidBy: {
            select: { id: true, firstName: true, lastName: true, email: true },
          },
        },
      });
      const paidAmount = roundMoney(
        Number(purchase.paidAmount) + dto.amount,
      );
      await tx.purchase.update({
        where: { id },
        data: { paidAmount },
      });
      return {
        ...payment,
        purchase: {
          id: purchase.id,
          invoiceNumber: purchase.invoiceNumber,
          companyName: purchase.company.name,
          total: purchase.total,
          paidAmount,
          dueAmount: roundMoney(Number(purchase.total) - paidAmount),
        },
      };
    });
  }

  private preparePurchase(dto: CreatePurchaseDto) {
    const items = dto.items.map((item) => ({
      ...item,
      expiryDate: new Date(item.expiryDate),
      total: calculateLineTotal(
        item.quantity,
        item.unitPrice,
        item.discount,
        item.vat,
      ),
    }));
    const subtotal = roundMoney(
      items.reduce(
        (sum, item) => sum + item.quantity * item.unitPrice,
        0,
      ),
    );
    const discount = roundMoney(
      items.reduce((sum, item) => sum + item.discount, 0),
    );
    const vat = roundMoney(items.reduce((sum, item) => sum + item.vat, 0));
    return {
      items,
      subtotal,
      discount,
      vat,
      total: roundMoney(subtotal - discount + vat),
    };
  }
}

@ApiTags('purchases')
@ApiBearerAuth()
@Permissions('purchases.view')
@Controller('purchases')
export class PurchasesController {
  constructor(private readonly service: PurchasesService) {}
  @Get()
  list() {
    return this.service.list();
  }
  @Post()
  create(@Body() dto: CreatePurchaseDto, @CurrentUser() user: CurrentUserData) {
    return this.service.create(dto, user.id);
  }
  @Patch(':id')
  update(@Param('id') id: string, @Body() dto: UpdatePurchaseDto) {
    return this.service.update(id, dto);
  }
  @Delete(':id')
  remove(@Param('id') id: string) {
    return this.service.remove(id);
  }
  @Get(':id/payments')
  @Roles(Role.SUPER_ADMIN, Role.ADMIN)
  listPayments(@Param('id') id: string) {
    return this.service.listPayments(id);
  }
  @Post(':id/payments')
  @Roles(Role.SUPER_ADMIN, Role.ADMIN)
  pay(
    @Param('id') id: string,
    @Body() dto: PurchasePaymentDto,
    @CurrentUser() user: CurrentUserData,
  ) {
    return this.service.pay(id, dto, user.id);
  }
  @Post(':id/confirm')
  confirm(@Param('id') id: string) {
    return this.service.confirm(id);
  }
}

class SaleItemDto {
  @IsUUID()
  productId!: string;
  @IsUUID()
  batchId!: string;
  @IsInt()
  @Min(1)
  quantity!: number;
  @IsNumber()
  @Min(0)
  unitPrice!: number;
  @IsOptional()
  @IsNumber()
  @Min(0)
  discount = 0;
  @IsOptional()
  @IsNumber()
  @Min(0)
  vat = 0;
}

class CreateSaleDto {
  @ApiProperty()
  @IsString()
  invoiceNumber!: string;
  @IsUUID()
  branchId!: string;
  @IsOptional()
  @IsString()
  customerName?: string;
  @IsOptional()
  @IsString()
  customerPhone?: string;
  @IsOptional()
  @IsString()
  customerType?: string;
  @IsArray()
  @ArrayMinSize(1)
  @ValidateNested({ each: true })
  @Type(() => SaleItemDto)
  items!: SaleItemDto[];
}

@Injectable()
export class SalesService {
  constructor(private readonly prisma: PrismaService) {}

  list() {
    return this.prisma.sale.findMany({
      include: { items: true, payments: true, createdBy: true },
      orderBy: { createdAt: 'desc' },
    });
  }

  create(dto: CreateSaleDto, userId: string) {
    return this.prisma.$transaction(
      async (tx) => {
        const items = dto.items.map((item) => ({
          ...item,
          total: calculateLineTotal(
            item.quantity,
            item.unitPrice,
            item.discount,
            item.vat,
          ),
        }));
        const subtotal = roundMoney(
          items.reduce(
            (sum, item) => sum + item.quantity * item.unitPrice,
            0,
          ),
        );
        const discount = roundMoney(
          items.reduce((sum, item) => sum + item.discount, 0),
        );
        const vat = roundMoney(
          items.reduce((sum, item) => sum + item.vat, 0),
        );
        const total = roundMoney(subtotal - discount + vat);
        const sale = await tx.sale.create({
          data: {
            invoiceNumber: dto.invoiceNumber,
            branchId: dto.branchId,
            customerName: dto.customerName,
            customerPhone: dto.customerPhone,
            customerType: dto.customerType,
            subtotal,
            discount,
            vat,
            total,
            dueAmount: total,
            createdById: userId,
            items: { create: items },
          },
          include: { items: true },
        });
        for (const item of items) {
          const updated = await tx.productBatch.updateMany({
            where: {
              id: item.batchId,
              productId: item.productId,
              branchId: dto.branchId,
              quantity: { gte: item.quantity },
            },
            data: { quantity: { decrement: item.quantity } },
          });
          if (updated.count !== 1) {
            throw new BadRequestException(
              `Insufficient stock for product ${item.productId}`,
            );
          }
          const batch = await tx.productBatch.findUniqueOrThrow({
            where: { id: item.batchId },
          });
          await tx.stockMovement.create({
            data: {
              productId: item.productId,
              branchId: dto.branchId,
              type: MovementType.SALE,
              quantity: -item.quantity,
              balanceAfter: batch.quantity,
              referenceType: 'Sale',
              referenceId: sale.id,
            },
          });
        }
        return sale;
      },
      { isolationLevel: Prisma.TransactionIsolationLevel.Serializable },
    );
  }
}

@ApiTags('sales')
@ApiBearerAuth()
@Permissions('sales.ipd', 'sales.opd', 'sales.staff', 'sales.internal')
@Controller('sales')
export class SalesController {
  constructor(private readonly service: SalesService) {}
  @Get()
  list() {
    return this.service.list();
  }
  @Post()
  create(@Body() dto: CreateSaleDto, @CurrentUser() user: CurrentUserData) {
    return this.service.create(dto, user.id);
  }
}

class PaymentDto {
  @IsOptional()
  @IsUUID()
  saleId?: string;
  @IsOptional()
  @IsUUID()
  purchaseId?: string;
  @IsEnum(PaymentMethod)
  method!: PaymentMethod;
  @IsNumber()
  @Min(0.01)
  amount!: number;
  @IsOptional()
  @IsString()
  reference?: string;
}

@ApiTags('payments')
@ApiBearerAuth()
@Controller('payments')
export class PaymentsController {
  constructor(private readonly prisma: PrismaService) {}
  @Get()
  list() {
    return this.prisma.payment.findMany({ orderBy: { paidAt: 'desc' } });
  }
  @Post()
  create(
    @Body() dto: PaymentDto,
    @CurrentUser() user: CurrentUserData,
  ) {
    if (!!dto.saleId === !!dto.purchaseId) {
      throw new BadRequestException(
        'Exactly one of saleId or purchaseId is required',
      );
    }
    return this.prisma.$transaction(async (tx) => {
      const payment = await tx.payment.create({
        data: { ...dto, paidById: user.id },
      });
      if (dto.saleId) {
        const sale = await tx.sale.findUniqueOrThrow({
          where: { id: dto.saleId },
        });
        const returned = await tx.return.aggregate({
          where: { saleId: dto.saleId },
          _sum: { total: true },
        });
        const payableTotal = roundMoney(
          Math.max(
            0,
            Number(sale.total) - Number(returned._sum.total ?? 0),
          ),
        );
        const paidAmount = roundMoney(
          Number(sale.paidAmount) + dto.amount,
        );
        if (paidAmount > payableTotal) {
          throw new BadRequestException('Payment exceeds sale total');
        }
        await tx.sale.update({
          where: { id: dto.saleId },
          data: {
            paidAmount,
            dueAmount: roundMoney(payableTotal - paidAmount),
          },
        });
      } else {
        const purchase = await tx.purchase.findUniqueOrThrow({
          where: { id: dto.purchaseId },
        });
        const paidAmount = roundMoney(
          Number(purchase.paidAmount) + dto.amount,
        );
        if (paidAmount > Number(purchase.total)) {
          throw new BadRequestException('Payment exceeds purchase total');
        }
        await tx.purchase.update({
          where: { id: dto.purchaseId },
          data: { paidAmount },
        });
      }
      return payment;
    });
  }
}

@Module({
  controllers: [PurchasesController, SalesController, PaymentsController],
  providers: [PurchasesService, SalesService],
})
export class TransactionsModule {}
